// ---- Attack-pose tuning: data-driven keyframe clips (per ARCHETYPE) ----
// The single source of truth for how each WEAPON ARCHETYPE poses the humanoid
// rig: its light-attack motion, its carry + ready stances, and its hold
// transform override — one self-contained block per archetype (one-hand-sword,
// bow, …). Every item that declares `weapon.archetype = X` shares X's set, so
// three iron swords all swing the same way. Unarmed (the fists) has a jab plus
// an optional combat-ready stance (the boxer guard); a small shared `moveClips`
// library backs the special moves (action-bar slots 2-3), which are cross-weapon
// and so keep their own named motions.
//
// The values are CONTENT — they live in content/anim/clips.json (schema-
// validated, machine-writable) so they can be tuned visually in the Model Studio
// and persisted with the studio's Apply button (PUT /api/content/anim/clips →
// validated write). pose.ts reads ARCHETYPE_ANIM / UNARMED / MOVE_CLIPS through
// the sampler below; the studio mutates these same objects in place for live
// preview, then serializes them back. The same per-archetype data drives the
// player AND every humanoid-contract rig (src/model/rig.ts).
//
// A clip is { duration, keys[] }. Each key has a normalized time `t` (0→1) and a
// sparse set of CHANNELS, each a [x,y,z] euler (radians). A channel is only
// animated if at least one key defines it — so a clip that touches only the left
// arm leaves every other joint to the gait pose. Channels are sampled
// independently (piecewise-linear across just the keys that define them), so you
// can key different joints at different times within one clip.
//
// The `grip` channel is special: it is NOT a joint, it's a DELTA added to the
// equipped weapon's hold.rot for the duration of the attack/stance (e.g. laying a
// blade forward out of the fist for a slash). Channels with no grip keep their
// natural hold orientation.

import raw from "../../content/anim/clips.json";
import { RIGS, type HumanoidJoint } from "../model/rig.ts";
import type { WeaponArchetypeId } from "./archetypes.ts";

export type Vec3 = [number, number, number];

/** Rig joints a clip can drive, plus the synthetic `grip` weapon-rotation delta.
 *  Joint channels ARE the rig-contract joint names (src/model/rig.ts) — one
 *  source of truth shared with the content schema and the studio clip editor. */
export type PoseChannel = HumanoidJoint | "grip";

/** Joint channels map 1:1 onto rig joint groups; `grip` is handled separately. */
export const JOINT_CHANNELS: readonly HumanoidJoint[] = RIGS.humanoid.joints;

export const ALL_CHANNELS: PoseChannel[] = [...JOINT_CHANNELS, "grip"];

export type Keyframe = { t: number } & Partial<Record<PoseChannel, Vec3>>;
export type AttackClip = { duration: number; keys: Keyframe[] };

/** A STANCE is a single static pose the rig holds while carrying a weapon and
 *  NOT mid-attack — the idle/carry pose (which the walk gait rides on top of) and
 *  the raised on-guard "ready" pose. Same channel format as a keyframe, minus the
 *  time (there's no timeline — it's one pose). `grip` works here too (a hold.rot
 *  delta) so a stance can re-angle the blade in the fist. */
export type StancePose = Partial<Record<PoseChannel, Vec3>>;

/** A per-archetype hold-transform override layer. Any field left out falls back
 *  to the weapon item's authored `hold`. `hilt` is NEVER overridden — it equals
 *  model `landmarks.grip` and is not a tuning knob. Prefer fixing item hold.rot
 *  as the base orientation; use this override only when the archetype anim truly
 *  needs a different base. Stance/clip `grip` is a small delta on top of that. */
export type HoldOverride = { rot?: Vec3; offset?: Vec3; scale?: number };

/**
 * Coupled upper-body walk/run rock for two-hand carry (applied in pose.ts when
 * armed, not combat-ready, and a CarrySway input is present). Hands rock as a
 * unit so dual grip doesn't shear. `false` = locked (no rock).
 */
export type CarrySwayTuning = {
  torsoY?: number;
  torsoZ?: number;
  torsoX?: number;
  rockMul?: number;
  elbowMul?: number;
  headY?: number;
  headZ?: number;
};

/** One archetype's complete animation set: its light-attack motion, carry + ready
 *  stances, hold-transform override, and optional carry locomotion rock — shared
 *  by every item of that archetype. */
export type WeaponAnim = {
  hold?: HoldOverride;
  attack: AttackClip;
  carry?: StancePose;
  ready?: StancePose;
  /** Upper-body rock on walk/run. Carry: full arms+torso. Combat-ready: torso/
   *  head (and 1H free hand) at half strength — weapon arm(s) stay planted.
   *  2H: coupled dual-arm rock on carry (or false to lock). 1H: free-arm sway +
   *  torso/head when set; omit = light free-arm only. */
  carrySway?: CarrySwayTuning | false;
};

/** Unarmed fists — the jab plus optional combat-ready / idle stances (no weapon
 *  hold). `ready` is the on-guard pose when `combatReady` (engaged); `carry` is
 *  unused for now but kept for symmetry with weapon anim sets. */
export type UnarmedAnim = {
  attack: AttackClip;
  ready?: StancePose;
  carry?: StancePose;
};

/** Root transform a body pose applies ON TOP of the joint pose. The rig root is
 *  the FEET (pelvis rigidly ~1.06·scale·legLen above — see src/model/pose-seat.ts),
 *  so sit/lie poses NEED a y drop; prone/all-fours add pitch. All values blend in
 *  with the pose fade and are ADDITIVE over the host's feetY resolve — the pose
 *  system never owns the root, the caller integrates (topple precedent). Facing
 *  (rotation.y) is never pose data. */
export type PoseRoot = { y: number; pitch?: number; roll?: number };

/** Support contacts a pose declares (pose-seat diagnostics + seat math). Mirrors
 *  POSE_CONTACTS in src/content/schemas.ts. */
export type PoseContact = "pelvis" | "knees" | "hands" | "feet" | "torso";

/** A NAMED FULL-BODY POSE (seated / sitting / prone / all-fours / kneeling /
 *  cinematic holds) — a StancePose plus root offset, cross-fade windows, and
 *  authoring metadata. Applied as a blended final override layer by
 *  src/anim/body-pose.ts on any humanoid-contract rig. */
export type BodyPose = {
  joints: StancePose;
  root?: PoseRoot;
  /** Cross-fade seconds into/out of the pose; omitted → POSE_BLEND defaults. */
  fade?: { enter: number; exit: number };
  contacts?: PoseContact[];
  /** THE furniture-sit pose — click-to-sit applies it. */
  seatable?: boolean;
  /** Cutscene-only: excluded from chat emotes + the studio pose row. */
  cinematic?: boolean;
  /** RESERVED (unimplemented): moveClips id for an authored enter transition. */
  enterClip?: string;
};

export type TuningData = {
  $schema?: string;
  /** Which joint set the clips drive — "humanoid" (the shared rig contract). */
  rig?: string;
  /** Unarmed fists — jab + optional combat-ready (boxer guard). */
  unarmed: UnarmedAnim;
  /** Per-archetype animation, keyed by WeaponArchetypeId. */
  archetypes: Record<string, WeaponAnim>;
  /** Shared special-move motions (content/moves `clip` refs). */
  moveClips: Record<string, AttackClip>;
  /** Named full-body poses (seated/prone/… + cinematic holds), keyed by pose id.
   *  Ids are wire strings (NetPlayer/NetNpc.pose) — never rename casually. */
  poses?: Record<string, BodyPose>;
};

// Deep-clone the imported JSON so these are freely MUTABLE (the studio edits them
// in place for live preview); the JSON import itself may be frozen/shared.
const data = structuredClone(raw) as TuningData;
if (data.rig === "player") data.rig = "humanoid"; // legacy rig name → the shared contract
data.unarmed ??= { attack: { duration: 0.35, keys: [{ t: 0 }] } };
data.archetypes ??= {};
data.moveClips ??= {};
data.poses ??= {};

/** Live per-archetype animation map. Keys are WeaponArchetypeId strings. */
export const ARCHETYPE_ANIM: Record<string, WeaponAnim> = data.archetypes;
export const UNARMED = data.unarmed;
export const MOVE_CLIPS: Record<string, AttackClip> = data.moveClips;

/** Live named body-pose map (studio-mutable, like ARCHETYPE_ANIM). */
export const BODY_POSES: Record<string, BodyPose> = data.poses;

export function bodyPose(id: string): BodyPose | undefined {
  return BODY_POSES[id];
}

/** THE shared pose-name list — models.ts PoseLink, inspect-model, the #/anim
 *  editor, chat emotes, and the server pose handler all resolve ids through
 *  here; never hand-list body pose names anywhere else. */
export function bodyPoseIds(): string[] {
  return Object.keys(BODY_POSES);
}

/** Poses a player may emote into. Excludes cinematic-only holds AND the
 *  seatable pose (it assumes a seat surface — furniture click-to-sit only);
 *  the server's pose handler enforces the same two exclusions. */
export function emotePoseIds(): string[] {
  return Object.keys(BODY_POSES).filter((id) => !BODY_POSES[id]!.cinematic && !BODY_POSES[id]!.seatable);
}

/** THE furniture-sit pose (`seatable: true`) — what click-to-sit applies. */
export function seatablePoseId(): string | null {
  for (const id of Object.keys(BODY_POSES)) if (BODY_POSES[id]!.seatable) return id;
  return null;
}

/** Snapshot the current tuning for persistence (studio Apply → PUT /api/content/anim/clips). */
export function serializeTuning(): TuningData {
  return {
    $schema: "../schema/anim-clips.schema.json",
    rig: data.rig ?? "humanoid",
    unarmed: UNARMED,
    archetypes: ARCHETYPE_ANIM,
    moveClips: MOVE_CLIPS,
    poses: BODY_POSES,
  };
}

/** Overwrite the live tuning objects from a snapshot IN PLACE, keeping their
 *  identity (pose.ts holds these references and samples them every frame). THE
 *  single restore path — shared by undo/redo and by the studio's Revert (both the
 *  #/anim view and the #/models dock) so the restore semantics can't drift. */
export function restoreTuning(d: TuningData): void {
  const src = d.archetypes ?? {};
  for (const k of Object.keys(ARCHETYPE_ANIM)) delete ARCHETYPE_ANIM[k];
  Object.assign(ARCHETYPE_ANIM, structuredClone(src));
  for (const k of Object.keys(MOVE_CLIPS)) delete MOVE_CLIPS[k];
  Object.assign(MOVE_CLIPS, structuredClone(d.moveClips ?? {}));
  for (const k of Object.keys(BODY_POSES)) delete BODY_POSES[k];
  Object.assign(BODY_POSES, structuredClone(d.poses ?? {}));
  if (d.unarmed?.attack) UNARMED.attack = structuredClone(d.unarmed.attack);
  // Stance fields may be added/removed across restores — clear then reassign so a
  // restore that drops `ready` doesn't leave a stale guard pose on the live object.
  delete UNARMED.ready;
  delete UNARMED.carry;
  if (d.unarmed?.ready) UNARMED.ready = structuredClone(d.unarmed.ready);
  if (d.unarmed?.carry) UNARMED.carry = structuredClone(d.unarmed.carry);
}

/** Look up (or seed) an archetype's anim set. Studio editors call this so a
 *  brand-new archetype is still editable before Apply. */
export function archetypeAnim(id: WeaponArchetypeId): WeaponAnim {
  return (ARCHETYPE_ANIM[id] ??= { attack: structuredClone(UNARMED.attack) });
}

/** Sample one channel's [x,y,z] at normalized time `k` (0→1), or null if the clip
 *  never animates that channel. Piecewise-linear across the keys that define it;
 *  clamps to the first/last such key outside their range. */
export function sampleChannel(clip: AttackClip, ch: PoseChannel, k: number): Vec3 | null {
  let prev: Keyframe | null = null;
  let next: Keyframe | null = null;
  for (const kf of clip.keys) {
    if (kf[ch] === undefined) continue;
    if (kf.t <= k) prev = kf;
    if (kf.t >= k && next === null) next = kf;
  }
  if (!prev && !next) return null;     // channel not animated by this clip
  if (!prev) return next![ch]!.slice() as Vec3; // before the first defining key
  if (!next) return prev[ch]!.slice() as Vec3;  // after the last defining key
  if (prev === next) return prev[ch]!.slice() as Vec3; // exactly on a key
  const span = next.t - prev.t || 1;
  const f = (k - prev.t) / span;
  const a = prev[ch]!, b = next[ch]!;
  return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f, a[2] + (b[2] - a[2]) * f];
}
